import { type UseMutationResult, type UseQueryResult, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { DateTime } from "luxon";
import queryString from "query-string";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useChosenDepartment } from "../../../contexts/ChosenDepartment";
import { useErrorSnackbar } from "../../../hooks/useErrorSnackbar";
import type { AdminRole } from "../../../utils/models";
import axiosInstance from "../../../utils/networkWrapper";
import type { ActivityLogIntegrationName } from "./activityLogIntegrations";
import { type ActivityLogGroupDetail, activityLogGroupDetailQueryKey } from "./useActivityLogGroupDetailQuery";

/**
 * `GroupedActivityLogEntry.details`'s shape for a tier-2 (Call Off) representative row — always a
 * shift-table row by definition, so it always carries the real shift context. Mirrors backend
 * `CallOffDetails` (`backend/models/team.py`) field-for-field.
 *
 * `kind` reuses `actionName`'s own vocabulary as the discriminant value (this codebase's "backend
 * computes closed-set ID" convention). Fields stay snake_case, UNALIASED — this whole `details` field
 * skips the API's usual camelCase conversion (a deliberate, pre-existing exception the frontend already
 * read by these exact keys before this type existed).
 */
export interface CallOffDetails {
	kind: "Call Off";
	has_open_shift: boolean;
	reason: string | null;
	shift_start: string | null;
	shift_end: string | null;
	location: string | null;
}

/** `GroupedActivityLogEntry.details`'s shape for a tier-6 (Shift) representative row — the same shift
 * context as `CallOffDetails` minus `has_open_shift` (a Call-Off-only concept). Mirrors backend
 * `ShiftTierDetails` (`backend/models/team.py`) — named `ShiftDetails` here per the confirmed FE naming,
 * distinct from the backend's own name (which collides with an unrelated, pre-existing scheduling-grid
 * `ShiftDetails` model). See `CallOffDetails`'s doc comment for the shared `kind`/unaliased conventions. */
export interface ShiftDetails {
	kind: "Shift";
	reason: string | null;
	shift_start: string | null;
	shift_end: string | null;
	location: string | null;
}

/** `GroupedActivityLogEntry.details`'s shape for a tier-3 (PTO) representative row — pre-formatted
 * display strings, its recorded reason, and status; never raw ISO timestamps. Mirrors backend
 * `PtoDetails`. See `CallOffDetails`'s doc comment for the shared `kind`/unaliased conventions. */
export interface PtoDetails {
	kind: "PTO";
	reason: string | null;
	status: string | null;
	date_range: string | null;
	duration: string | null;
}

/** `GroupedActivityLogEntry.details`'s shape for a tier-4 (Request) representative row — shared by
 * `requests` inserts and standalone updates, plus `requests_status` outcomes. Mirrors backend
 * `RequestDetails`. See `CallOffDetails`'s doc comment for the shared `kind`/unaliased conventions. */
export interface RequestDetails {
	kind: "Request";
	request_type: string | null;
	status: string | null;
	shift_start: string | null;
	shift_end: string | null;
	location: string | null;
	job: string | null;
	needed_nurses: number | null;
}

/** `GroupedActivityLogEntry.details`'s shape for a tier-7 (Other) representative row — the raw
 * table_name + changes passthrough for any table the significance ladder doesn't otherwise recognize.
 * Mirrors backend `OtherDetails`. See `CallOffDetails`'s doc comment for the shared `kind` convention. */
export interface OtherDetails {
	kind: "Other";
	table_name: string;
	changes: Record<string, unknown> | null;
}

/** `GroupedActivityLogEntry.details`'s shape for a tier-5 (Recurring) representative row (2026-09-07) —
 * mirrors `ShiftDetails` plus `recurring_params` (the `recurring_shift` row's own JSONB
 * `{recurring_type, weekdays, ...}` blob). `shift_start`/`shift_end` here are bare Postgres `Time`
 * strings ("07:00:00"), NOT ISO datetimes — same shape `GroupDetailPanel.tsx`'s own
 * `rowDetailSublabel` already reads off a `recurring_shift` row's raw `changes`, format with
 * `formatRecurringShiftTimeRange`, never `formatShiftDetailRange`. Mirrors backend
 * `RecurringShiftDetails`. See `CallOffDetails`'s doc comment for the shared `kind`/unaliased
 * conventions. */
export interface RecurringDetails {
	kind: "Recurring";
	reason: string | null;
	shift_start: string | null;
	shift_end: string | null;
	location: string | null;
	recurring_params: Record<string, unknown> | null;
}

/** Real TS discriminated union (narrow on `.kind`) mirroring the backend's own Pydantic discriminated
 * union (`GroupedActivityLogEntry.details`, `backend/models/team.py`) — replaces the previous untyped
 * `Record<string, unknown> | null` passthrough now that every tier's shape is confirmed and stable. */
export type GroupedActivityLogEntryDetails = CallOffDetails | ShiftDetails | PtoDetails | RequestDetails | OtherDetails | RecurringDetails;

/** A scheduled-shift field a shift's history reports; the value is its `nurse_in_shift` column. Mirrors backend `ShiftChangeField`. */
export enum ShiftChangeField {
	Start = "start_time",
	End = "end_time",
	Location = "location",
	Job = "job_id",
	Notes = "notes",
	Charge = "is_shift_manager",
	Bonus = "bonus",
	CallInTime = "call_in_time",
	Removed = "deleted",
	RemovalReason = "message",
	RemovalMessage = "removal_message",
	TrainingStatus = "training_status",
	ShiftType = "shift_type",
	Published = "is_published",
}

/** How a scheduled shift got on the schedule, read from its row as inserted. Mirrors backend `ShiftOrigin`. */
export enum ShiftOrigin {
	Recurring = "recurring",
	Split = "split",
	Request = "request",
	Swap = "swap",
	Imported = "imported",
	CopiedFromWorked = "copied_from_worked",
	Added = "added",
}

/** One field a transaction changed on a shift, as it found it and as it left it. Mirrors backend `ShiftFieldChange`. */
export interface ShiftFieldChange {
	field: ShiftChangeField;
	before: string | null;
	after: string | null;
}

/**
 * One row per `api_request_id` transaction group — the significance-ladder-ranked representative
 * row for that group, plus group-level metadata. Mirrors `GroupedActivityLogEntry`
 * (`backend/models/team.py`) field-for-field; hand-written since this repo has no OpenAPI generation.
 *
 * `kind: "resolved"` — every group under the backend's row-count threshold (500 rows, `DAT-…` perf
 * fix), i.e. still the vast majority of groups in every department. Fully populated, unchanged shape.
 */
export interface ResolvedGroupedActivityLogEntry {
	kind: "resolved";
	apiRequestId: string;
	departmentId: string;
	changedAt: string;
	/** `Optional[str] = Field(alias="changedUser", default=None)` on the backend serializes as JSON
	 * `null` (no `exclude_none`), not an omitted key — so this must be `string | null`, not `string |
	 * undefined`, same null-vs-undefined convention already applied to `performedFor`/`details` below. */
	changedUser: string | null;
	actionName: string;
	/** Rows sharing THIS api_request_id (this transaction). */
	groupSize: number;
	/** Rows `/groups/{id}` will actually return — usually == groupSize, but for Request/open-shift-flagged
	 * Call-Off it's the requests.id lineage count instead, which can span many other transactions. */
	expandRowCount: number;
	isExpandable: boolean;
	/** Same `Optional[str] = Field(default=None)`-with-no-`exclude_none` shape as `changedUser` above. */
	performedBy: string | null;
	/** The acting user's first name, set only when `performedBy` is a real username this caller sees
	 * unmasked (never for "Automation"/"Call off – …" labels or support/system buckets). Optional
	 * because this bundle can reach a backend that predates the field; `performedBy` stays the username. */
	performedByFirstName?: string | null;
	/** The agency integration (Clipboard, Nursa) that made this change; `performedBy` then equals it. `null`
	 * for every other change.
	 * DEFAULT: optional because this bundle can reach a backend that predates the field; absent reads as `null`. */
	integrationName?: ActivityLogIntegrationName | null;
	/** The agency the affected worker comes through, set only alongside `integrationName` and only when the
	 * group's workers name exactly one agency; `null` otherwise.
	 * DEFAULT: optional for the same older-backend reason as `integrationName`; absent reads as `null`. */
	agencyName?: string | null;
	/** `Optional[int] = None` on the backend serializes as JSON `null` (no `exclude_none`), not an
	 * omitted key — so this must be `number | null`, not `number | undefined`. */
	performedFor: number | null;
	/** The subject as a NAME instead of an id, set only where an id cannot work: a hard-deleted employee.
	 * That row's `staff_change_log.nurse` is necessarily NULL (FK with ON DELETE CASCADE), so
	 * `performedFor` is null — and the nurse is gone from `HospitalDataProvider`'s list anyway, so
	 * resolving one by id would render blank AND log a false `invalid_nurse_id` event. `null` for every
	 * other row, which still resolves by id. Same null-vs-undefined shape as `changedUser` above. */
	performedForName: string | null;
	/** Set (with `performedFor` left null) ONLY when a group genuinely spans more than one distinct
	 * nurse and isn't a swap or an approved replacement — a Bulk-Update-sized `nurse`-table operation is
	 * the real-world case (2026-09-06 fix). `null` for the vast-majority single-nurse case, and also
	 * `null` for a swap group or an approved replacement (their own `swapOutNurseId`/`swapInNurseId` and
	 * `replacedNurseId`/`replacingNurseId` pairings below already convey their 2 nurses specifically).
	 * Same null-vs-undefined shape as `changedUser` above. */
	affectedNurseCount: number | null;
	/** Swap requests pair two nurses (out/in) — performedFor stays null for a swap group. */
	swapOutNurseId: number | null;
	swapInNurseId: number | null;
	/** An approved replacement pairs the nurse replaced with the nurse who took the shift — performedFor
	 * stays null for it, same as a swap.
	 * DEFAULT: optional because this bundle can reach a backend that predates the fields; absent reads as
	 * "no pair", the same as null. Make them required once the backend that sends them is deployed. */
	replacedNurseId?: number | null;
	replacingNurseId?: number | null;
	/** Same `Optional[str] = Field(default=None)`-with-no-`exclude_none` shape as `changedUser` above. */
	channel: string | null;
	/** Same `Optional[str] = Field(default=None)`-with-no-`exclude_none` shape as `changedUser` above. */
	what: string | null;
	/** `True` only when this group's tier-6 representative row is a `nurse_in_shift_actual` Insert (a
	 * real punch), never when `what` itself changes text — `what` is always "Shift Added"/"Shift
	 * Updated"/"Shift Removed" regardless of table. Drives the outer grid's "Punched" badge alongside
	 * the unchanged base `what` text (see `GroupedActivityLog.tsx`'s "What" column). */
	isNurseActualPunch: boolean;
	/**
	 * Tier-specific extras — a real discriminated union (`GroupedActivityLogEntryDetails` above),
	 * narrow on `.kind`. KEYS stay backend snake_case (`shift_start`, not `shiftStart`; this one field
	 * skips the API's usual alias/camelCase conversion). `Optional[Union[...]] = Field(default=None)`
	 * with no `exclude_none` serializes an unset value as JSON `null`, not an omitted key — so this must
	 * be `GroupedActivityLogEntryDetails | null`, not `| undefined` (the same null-vs-undefined fix
	 * already applied to `performedFor` above).
	 */
	details: GroupedActivityLogEntryDetails | null;
	/** Set only on a shift-scoped read (`shiftId`): what this transaction changed on that shift; `null` otherwise. */
	shiftChanges: ShiftFieldChange[] | null;
	/** Set only on a shift-scoped read's entry whose transaction added that shift: how it was added; `null` otherwise.
	 * DEFAULT: optional because this bundle can reach a backend that predates the field; absent reads as `null`. */
	shiftOrigin?: ShiftOrigin | null;
}

/**
 * `kind: "deferred"` — a group over the backend's row-count threshold (500 rows): a punch-clock sync,
 * a bulk PBJ recompute, or any other `api_request_id` transaction large enough that resolving its full
 * representative-row significance ladder on every list fetch would defeat the point of the perf fix.
 * Genuinely minimal — no `actionName`/`what`/`isExpandable`/`performedBy`/etc, because the backend
 * hasn't computed them yet. `changedAt` is the group's own MAX `changed_at` (its most recent activity),
 * same meaning as the resolved shape's field of the same name. Resolve one on demand via
 * `useResolveActivityLogGroupMutation` (`GET .../groups/{api_request_id}/resolve`) — see
 * `GroupedActivityLog.tsx`'s `onRowExpandRequest`, which fires when the user opens the row's expand
 * chevron and lets the grid open the row once the resolved entry lands.
 *
 * The backend silently EXCLUDES deferred groups from the list response whenever a filter that needs
 * per-group data is active (`performedFor`/`performedBy`/`channel`/a shift-window filter/`shiftJobIds`)
 * — a deliberate product decision, not a bug. `actionNames` is NOT one of these (still matched normally
 * against deferred groups). See `GroupedActivityLogFilters.tsx`'s exclusion hint.
 */
export interface DeferredGroupedActivityLogEntry {
	kind: "deferred";
	apiRequestId: string;
	departmentId: string;
	changedAt: string;
	/** Rows sharing THIS api_request_id (this transaction) — the only size information available before
	 * resolving. */
	groupSize: number;
	/** Set only on a shift-scoped read's entry whose transaction added that shift: how it was added; `null` otherwise.
	 * DEFAULT: optional because this bundle can reach a backend that predates the field; absent reads as `null`. */
	shiftOrigin?: ShiftOrigin | null;
}

/** Real TS discriminated union (narrow on `.kind`) mirroring the backend's own Pydantic discriminated
 * union (`GroupedActivityLogListEntry`, `backend/models/team.py`). */
export type GroupedActivityLogEntry = ResolvedGroupedActivityLogEntry | DeferredGroupedActivityLogEntry;

export interface GroupedActivityLogFilters {
	/**
	 * Optional: when omitted, the route falls back to its own default window
	 * (`GROUPED_ACTIVITY_LOG_DEFAULT_WINDOW` in `routes/activity_log.py`).
	 */
	actionTimeStart?: DateTime;
	actionTimeEnd?: DateTime;
	performedFor?: number;
	performedBy?: string[];
	/** A group matches when any of its changers holds any of the selected roles. */
	adminRoles?: AdminRole[];
	channel?: string[];
	actionNames?: string[];
	/** Specific Shift filter — `shiftWindowStart`/`shiftWindowEnd` are a composed absolute datetime PAIR
	 * (the FE combines a single date + start/end time client-side before this hook ever sees them — see
	 * `GroupedActivityLog.tsx`'s `composeShiftWindow`), always sent together or not at all.
	 * `shiftJobIds` is independently optional (a job filter with no date, or vice versa, both valid). */
	shiftWindowStart?: DateTime;
	shiftWindowEnd?: DateTime;
	shiftJobIds?: string[];
	/** Only the transactions that wrote this scheduled shift's own row, whatever its times were then. */
	shiftId?: string;
	/** Not optional like the rest — the backend defaults this to `false` server-side, but the FE always
	 * has an explicit current value (the "Punches" toggle) once it renders, so there's no "not yet
	 * chosen" state to omit like `actionTimeStart`/`actionTimeEnd` above. */
	includePunches: boolean;
	/** Specific Shift filter's "Exact times" toggle — switches the shift-window match from overlap to
	 * exact equality on the backend (`exact_shift_times`, default `true` there). Not optional, same
	 * reasoning as `includePunches` above: `GroupedActivityLog.tsx` always has an explicit current value
	 * (`shiftExactTimesApplied`, already clamped to `false` whenever `shiftWindowStart`/`shiftWindowEnd`
	 * aren't both real times — see its own comment) by the time this hook runs. */
	shiftExactTimes: boolean;
}

const GROUPED_ACTIVITY_LOG_QUERY_KEY = "activity-log-grouped";

export const useGroupedActivityLogQuery = (
	{
		actionTimeStart,
		actionTimeEnd,
		performedFor,
		performedBy,
		adminRoles,
		channel,
		actionNames,
		shiftWindowStart,
		shiftWindowEnd,
		shiftJobIds,
		shiftId,
		includePunches,
		shiftExactTimes,
	}: GroupedActivityLogFilters,
	{ enabled }: { enabled: boolean },
): UseQueryResult<GroupedActivityLogEntry[], Error> => {
	const { chosenDepartment } = useChosenDepartment();
	const showErrorSnackbar = useErrorSnackbar();
	const { t } = useTranslation();
	const { track, trackError } = useAnalytics();

	return useQuery({
		queryKey: [
			GROUPED_ACTIVITY_LOG_QUERY_KEY,
			chosenDepartment?.department_id,
			actionTimeStart?.toISO(),
			actionTimeEnd?.toISO(),
			performedFor,
			performedBy?.slice().sort().join(","),
			adminRoles?.slice().sort().join(","),
			channel?.slice().sort().join(","),
			actionNames?.slice().sort().join(","),
			shiftWindowStart?.toISO(),
			shiftWindowEnd?.toISO(),
			shiftJobIds?.slice().sort().join(","),
			shiftId,
			includePunches,
			shiftExactTimes,
		],
		queryFn: async () => {
			track("activity_log_grouped_fetch_start", { department_id: chosenDepartment?.department_id });

			const { data } = await axiosInstance.get<GroupedActivityLogEntry[]>(`/activity-log/departments/${chosenDepartment.department_id}/grouped/v2`, {
				params: {
					...(actionTimeStart ? { action_time_start: actionTimeStart.toISO() } : {}),
					...(actionTimeEnd ? { action_time_end: actionTimeEnd.toISO() } : {}),
					...(performedFor !== undefined ? { performed_for: performedFor } : {}),
					...(performedBy && performedBy.length > 0 ? { performed_by: performedBy } : {}),
					...(adminRoles && adminRoles.length > 0 ? { admin_roles: adminRoles } : {}),
					...(channel && channel.length > 0 ? { channel } : {}),
					...(actionNames && actionNames.length > 0 ? { action_names: actionNames } : {}),
					...(shiftWindowStart ? { shift_window_start: shiftWindowStart.toISO() } : {}),
					...(shiftWindowEnd ? { shift_window_end: shiftWindowEnd.toISO() } : {}),
					...(shiftJobIds && shiftJobIds.length > 0 ? { shift_job_ids: shiftJobIds } : {}),
					...(shiftId !== undefined ? { shift_id: shiftId } : {}),
					include_punches: includePunches,
					exact_shift_times: shiftExactTimes,
				},
				// FastAPI's repeated-key `list[str] = Query()` params need `arrayFormat: "none"` — the same
				// convention `backend.service.ts`'s `getCallOffsWithLimit` uses for its own list params.
				paramsSerializer: (params) => queryString.stringify(params, { arrayFormat: "none" }),
			});

			track("activity_log_grouped_fetch_success", { department_id: chosenDepartment?.department_id, entry_count: data.length });
			return data;
		},
		// DEFAULT: shift history must fetch again when reopened after a save; the broad Activity Log keeps its one-minute cache.
		staleTime: shiftId === undefined ? 60 * 1000 : 0,
		throwOnError: false,
		enabled: enabled && !!chosenDepartment?.department_id,
		meta: {
			onError: (error: unknown) => {
				showErrorSnackbar(t("activityLog.grouped.error"), { action: "failed to fetch grouped activity log" }, error);
				trackError("activity_log_grouped_fetch_error", {
					error_message: (error as Error)?.message,
					department_id: chosenDepartment?.department_id,
					error_source: "API",
				});
			},
		},
	});
};

/** `useResolveActivityLogGroupMutation`'s `mutate` variables — `includePunches` mirrors the same
 * "Punches" toggle value `useActivityLogGroupDetailQuery`/`GroupDetailPanel` already thread into the
 * sibling detail-expand endpoint, so a deferred group whose representative row is a punch resolves
 * consistently with whatever the rest of the page is currently showing. */
export interface ResolveActivityLogGroupVariables {
	apiRequestId: string;
	includePunches: boolean;
}

/** Mirrors `ResolvedActivityLogGroup` (`backend/models/team.py`): `/resolve` returns a wrapper, not the
 * entry, so the detail rows can ride along. `detail` is `null` unless `include_details=true`. */
export interface ResolveActivityLogGroupResponse {
	entry: ResolvedGroupedActivityLogEntry;
	detail: ActivityLogGroupDetail | null;
}

/** Sent unconditionally from this one call site. The backend gates the payload behind the flag so
 * disabling it is this single line, with no backend deploy, if it ever proves too heavy. */
const RESOLVE_INCLUDE_DETAILS = true;

/**
 * Deadline for one `/resolve`. `SystemDataGrid` has no timeout of its own — `onRowExpandRequest`'s
 * contract puts that on the caller — and without one a request that never answers leaves the row's
 * chevron spinning for the life of the mount with no way to retry. Axios aborts the request itself on
 * expiry, so this stops the network call rather than just abandoning its promise; the rejection then
 * takes the normal `onError` path (snackbar + tracked) and the row becomes retryable.
 */
const RESOLVE_TIMEOUT_MS = 60_000;

/**
 * Manually-triggered (never auto-fetched) resolve of ONE deferred group into its full `kind: "resolved"`
 * shape — a mutation rather than `useQuery`'s `enabled` gate, since this fires per-row when the user opens
 * a deferred row rather than whenever some id prop is set (mirrors `useSyncShifts`'s own on-demand
 * `useMutation` shape). Deliberately does NOT touch the `activity-log-grouped` list query's cache —
 * `GroupedActivityLog.tsx` swaps the resolved row into its own local override map instead, so a resolve
 * never invalidates or reorders the list under the user.
 *
 * The caller awaits **`mutateAsync`** and reads the outcome from the returned promise — never `mutate`
 * with per-call `{onSuccess, onError}` callbacks, and never this hook's shared `isPending`/`data`. That is
 * a correctness requirement, not a style choice: in TanStack Query v5 a second `mutate()` on the same
 * observer detaches it from the first mutation, so with two rows resolving concurrently the FIRST row's
 * per-call `onSuccess` would never fire and its row would stay unresolved forever. A promise belongs to
 * its own call and is immune to that, which is what makes several rows resolvable at once.
 *
 * It does write to the `activity-log-group-detail` cache (`onSuccess` below) — a different key space;
 * the list cache is still never touched. That seeding is what makes expanding a resolved row instant.
 */
export const useResolveActivityLogGroupMutation = (): UseMutationResult<ResolveActivityLogGroupResponse, Error, ResolveActivityLogGroupVariables> => {
	const { chosenDepartment } = useChosenDepartment();
	const showErrorSnackbar = useErrorSnackbar();
	const { t } = useTranslation();
	const { track, trackError } = useAnalytics();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ apiRequestId, includePunches }: ResolveActivityLogGroupVariables) => {
			track("activity_log_grouped_resolve_start", { department_id: chosenDepartment?.department_id, api_request_id: apiRequestId });

			// `include_punches` (2026-09-09 hotfix) — without it the backend defaults to `False` and 404s any
			// deferred group whose representative row is a punch (`nurse_in_shift_actual`, tier 6); mirrors
			// exactly how `useActivityLogGroupDetailQuery`'s sibling detail-expand endpoint already sends it.
			const { data } = await axiosInstance.get<ResolveActivityLogGroupResponse>(
				`/activity-log/departments/${chosenDepartment.department_id}/groups/${apiRequestId}/resolve`,
				{ params: { include_punches: includePunches, include_details: RESOLVE_INCLUDE_DETAILS }, timeout: RESOLVE_TIMEOUT_MS },
			);

			track("activity_log_grouped_resolve_success", {
				department_id: chosenDepartment?.department_id,
				api_request_id: apiRequestId,
				detail_row_count: data.detail?.rows.length ?? null,
			});
			return data;
		},
		onSuccess: (data, { apiRequestId, includePunches }) => {
			if (!data.detail) return;
			// `full: false` matches `GroupDetailPanel`'s initial `isFull`, so the panel reads this entry and
			// issues no request. Key MUST come from the shared builder; a literal here fails silently.
			queryClient.setQueryData(activityLogGroupDetailQueryKey(chosenDepartment?.department_id, apiRequestId, false, includePunches), data.detail);
		},
		onError: (error, { apiRequestId }) => {
			showErrorSnackbar(t("activityLog.grouped.deferred.error"), { action: "failed to resolve deferred activity log group" }, error);
			trackError("activity_log_grouped_resolve_error", {
				error_message: (error as Error)?.message,
				department_id: chosenDepartment?.department_id,
				api_request_id: apiRequestId,
				error_source: "API",
			});
		},
	});
};
